import moment from 'moment'
import { message } from 'antd'
import { generateEmailHTML } from 'email'
import { saveAs } from 'file-saver'
import { Service } from 'services'
import { DATE_FORMAT } from 'constants/app'
import { transformTimeStep } from 'utils'
import { generateTidesPdfBase64 } from 'utils/pdf'
import { API_URL } from 'constants/api'

/**
 * Handle analysis mailing with optional separate pilot graph
 * @param {object} analysis - Analysis object
 * @param {function} t - Translation function
 * @param {HTMLImageElement} img - Main graph image (Sans pilote for pilot scenarios)
 * @param {HTMLImageElement} imgPilot - Optional pilot graph image (Avec pilote for pilot scenarios)
 */
export async function handleAnalysisMailing(analysis, t, img, imgPilot = null) {
    try {
        const levels_start = await Service.list.levels(analysis.start_manoeuver_hour, [-12, 24])
        const levels_end = await Service.list.levels(analysis.end_manoeuver_hour, [-12, 24])
        const dockInfos = await Service.get.dock(analysis.dock_id)

        const { attachments: attachs, warnings } = await loadAttachments()
        const dockLabel = dockInfos?.label || ''
        const filename = `${analysis.boat_name} - ${moment(analysis.date).format(DATE_FORMAT)}.eml`
        const subject = `Départ à fort tirant d'eau / DEEP DRAFT DEPARTURE – ${analysis.boat_name} – Quebec Section ${dockLabel}`

        // Generate tides PDF
        const tidesAroundDeparture = transformTimeStep(levels_end, 15)
        const highTides = JSON.parse(analysis.highest_tides).filter(t => t != null)
        const dockDepth = analysis.dock_depth || dockInfos?.depth || 0
        const tidesPdfBase64 = generateTidesPdfBase64({
            tidesAroundDeparture,
            highTides,
            boatDraft: analysis.boat_draft,
            dockDepth,
        })

        const content = [
            `X-Unsent: 1`,
            `Subject: ${subject}`,
            `Content-Type: multipart/mixed; boundary=--boundary_text_string`,
            ``,
            `----boundary_text_string`,
            `Content-Type: text/html; charset=UTF-8`,
            ``,
            `${generateEmailHTML(analysis, levels_start, levels_end, img?.src, imgPilot?.src)}`,
            // Tides PDF attachment
            ``,
            `----boundary_text_string`,
            `Content-Type: application/pdf; name="Marees-Tides.pdf"`,
            `Content-Transfer-Encoding: base64`,
            `Content-Disposition: attachment; filename="Marees-Tides.pdf"`,
            ``,
            wrapBase64(tidesPdfBase64),
            // Static PDF attachments
            ...attachs.map(attach =>
                [
                    ``,
                    `----boundary_text_string`,
                    `Content-Type: application/pdf; name="${attach.name}"`,
                    `Content-Transfer-Encoding: base64`,
                    `Content-Disposition: attachment; filename="${attach.name}"`,
                    ``,
                    wrapBase64(attach.base64),
                ].join('\n')
            ),
            ``,
            `----boundary_text_string--`,
        ]

        // Conditionally add Cc line if email_bccs is not null
        if (dockInfos.email_bccs) {
            content.splice(2, 0, `Cc: ${dockInfos.email_bccs}`)
        }

        saveAs(new Blob([content.join('\n')], { type: 'text/plain;charset=utf-8' }), filename)

        if (warnings.length > 0) {
            message.warning(warnings.join(' — '), 8)
        }
    } catch (e) {
        message.error(t('general.feedback.error.title'))
    }
}

function blobToBase64(blob) {
    const reader = new FileReader()
    reader.readAsDataURL(blob)
    return new Promise(resolve => {
        reader.onloadend = () => {
            resolve(reader.result)
        }
    })
}

/**
 * Wrap base64 string at 76 characters for EML format
 * Handles various data URI formats including jsPDF's format with filename
 */
function wrapBase64(base64) {
    // Remove data URI prefix (handles various formats like data:application/pdf;base64, or data:application/pdf;filename=x;base64,)
    const raw = base64.replace(/^data:[^,]+,/, '')
    return raw.match(/.{1,76}/g)?.join('\n') || raw
}

export function filenameFromUrl(url) {
    try {
        const pathname = new URL(url).pathname
        return decodeURIComponent(pathname.split('/').pop()) || 'attachment.pdf'
    } catch {
        return 'attachment.pdf'
    }
}

async function loadAttachments() {
    const records = await Service.list.emailAttachments()
    const warnings = []

    const results = await Promise.all(
        records.map(async record => {
            try {
                const response = await fetch(`${API_URL}/email_attachments/${record.id}/download`)
                if (!response.ok) throw new Error(`HTTP ${response.status}`)
                const blob = await response.blob()
                const base64 = await blobToBase64(blob)
                return { name: filenameFromUrl(record.url), base64 }
            } catch (err) {
                warnings.push(`Failed to fetch "${filenameFromUrl(record.url)}": ${err.message}`)
                return null
            }
        })
    )

    return { attachments: results.filter(Boolean), warnings }
}
